import api from '../plugins/axios';

const GOOGLE_CLIENT_ID = process.env.VUE_APP_GOOGLE_CLIENT_ID;
const REDIRECT_URI = process.env.VUE_APP_REDIRECT_URI;
const GOOGLE_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';

export const authService = {
  initiateGoogleLogin() {
    const params = new URLSearchParams({
      client_id: GOOGLE_CLIENT_ID,
      redirect_uri: REDIRECT_URI,
      response_type: 'code',
      scope: 'openid email profile',
      access_type: 'offline',
      prompt: 'consent'
    });

    const url = `${GOOGLE_AUTH_URL}?${params.toString()}`;
    window.location.href = url;
  },

  async handleAuthCallback(code) {
    try {
      console.time('[authService.handleAuthCallback] /grantcode');

      const requestConfig = { timeout: 120000 };

      const attemptPost = async (tries) => {
        let lastErr;
        for (let i = 1; i <= tries; i++) {
          try {
            // Updated to match new backend prefix
            return await api.post('/auth/grantcode', { code }, requestConfig);
          } catch (e) {
            lastErr = e;
            const isTimeout = e?.code === 'ECONNABORTED' || (!e?.response && e?.message?.includes('timeout'));
            if (!isTimeout) throw e;
            const backoffMs = i === 1 ? 2000 : 5000;
            console.warn(`[authService.handleAuthCallback] timeout, retrying in ${backoffMs}ms`);
            await new Promise(r => setTimeout(r, backoffMs));
          }
        }
        throw lastErr;
      };

      const response = await attemptPost(3);
      console.timeEnd('[authService.handleAuthCallback] /grantcode');

      // 4. Map the backend response { token, refreshToken }
      //    to what the frontend expects { accessToken, refreshToken }
      const { token, refreshToken } = response.data;
      if (!token) {
        throw new Error("No token received from backend");
      }
      
      return { accessToken: token, refreshToken };

    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      const url = error?.config?.url;
      const method = error?.config?.method;
      console.error('[authService.handleAuthCallback] error', { status, url, method, message: error?.message, code: error?.code, data });
      throw error;
    }
  },

  async logout() {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      console.warn('[authService.logout] Failed to notify backend of logout', e);
    }
  },

  // 5. This helper (if used) should also use 'api' and the correct path
  async refreshTokens(refreshToken) {
    // Note: The axios interceptor handles this automatically,
    // but this function is now correct if you ever need to call it manually.
    // Use the updated endpoint
    const resp = await api.post('/auth/refresh-token', { refreshToken });
    // Handle both formats
    const { accessToken, token, refreshToken: newRefreshToken } = resp.data;
    return { accessToken: accessToken || token, refreshToken: newRefreshToken };
  },
  async fetchProfile() {
    return await api.get('/symbol-service/api/v1/users/me');
  },

  // Legacy methods - kept for backwards compatibility
  async completeOnboarding() {
    return await this.completeTour('main');
  },

  async completePortfolioOnboarding() {
    // Complete all portfolio-related tours
    await this.completeTour('portfolio');
    await this.completeTour('transactions');
    return await this.completeTour('analysis');
  },

  // New tour progress methods
  /**
   * Mark a specific tour as completed
   * @param {string} tourKey - The tour key (from TOUR_KEYS)
   */
  async completeTour(tourKey) {
    return await api.post('/symbol-service/api/v1/users/complete-tour', { tourKey });
  },

  /**
   * Update the current step of a tour (for saving mid-tour progress)
   * @param {string} tourKey - The tour key
   * @param {number} currentStep - The current step index
   */
  async updateTourProgress(tourKey, currentStep) {
    return await api.patch('/symbol-service/api/v1/users/tour-progress', { 
      tourKey, 
      currentStep 
    });
  },

  /**
   * Get the full onboarding progress object
   */
  async getOnboardingProgress() {
    return await api.get('/symbol-service/api/v1/users/onboarding-progress');
  },

  /**
   * Mark a tour as skipped (completed but user chose to skip)
   * @param {string} tourKey - The tour key
   */
  async skipTour(tourKey) {
    return await api.post('/symbol-service/api/v1/users/skip-tour', { tourKey });
  },

  /**
   * Skip all remaining tours (when user clicks "Skip All" in the modal)
   * @param {string[]} tourKeys - Array of tour keys to skip
   */
  async skipAllTours(tourKeys) {
    const promises = tourKeys.map(key => this.skipTour(key));
    return await Promise.all(promises);
  }
};
